import * as React from "react"
import PropTypes from "prop-types"
import { useStaticQuery, graphql } from "gatsby"

/**
 * Helper function to create a JSON-LD script element for structured data
 */
export const JsonLd = (data) => {
  return (
    <script 
      key="jsonld-script" 
      type="application/ld+json" 
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} 
    />
  );
}

/**
 * SEO component that exports all the necessary SEO elements
 * for use in Gatsby's Head API
 */
const Seo = ({ description, meta = [], title, canonicalUrl, structuredData }) => {
  const data = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            description
            exoclickSiteVerification
            title
            siteUrl
            social {
              twitter
            }
            rating
          }
        }
      }
    `
  )

  // Use our custom git info implementation instead of gatsby-source-local-git
  const gitCommitHash = process.env.GATSBY_APP_VERSION || 'development'
  
  // Use site description as fallback if no description is provided
  const metaDescription = description || data.site.siteMetadata.description
  const siteTitle = data.site.siteMetadata?.title
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle

  // Create an array of SEO elements
  const elements = [];
  
  // Preconnect links for performance optimization
  // Prefetch Gatsby data files instead of preloading to avoid console warnings
  elements.push(<link key="gatsby-prefetch" rel="prefetch" href="/page-data/app-data.json" as="fetch" crossOrigin="anonymous" />);
  elements.push(<link key="home-prefetch" rel="prefetch" href="/page-data/index/page-data.json" as="fetch" crossOrigin="anonymous" />);
  
  // Google Analytics
  elements.push(<link key="ga-preconnect" rel="preconnect" href="https://analytics.google.com" crossOrigin="anonymous" />);
  elements.push(<link key="ga-dns" rel="dns-prefetch" href="https://analytics.google.com" />);
  elements.push(<link key="ga-www-preconnect" rel="preconnect" href="https://www.google-analytics.com" crossOrigin="anonymous" />);
  elements.push(<link key="ga-www-dns" rel="dns-prefetch" href="https://www.google-analytics.com" />);
  
  // Sentry
  elements.push(<link key="sentry-preconnect" rel="preconnect" href="https://o271281.ingest.sentry.io" crossOrigin="anonymous" />);
  elements.push(<link key="sentry-dns" rel="dns-prefetch" href="https://o271281.ingest.sentry.io" />);
  
  // NOTE: Content Security Policy should be implemented via HTTP headers, not meta tags.
  // CSP meta tags may be ignored by browsers as indicated by the warning:
  // "The report-only Content Security Policy was delivered via a <meta> element, which is disallowed. The policy has been ignored."
  // Implement CSP through Firebase Hosting headers configuration in firebase.json instead.
  
  // PayPal
  elements.push(<link key="paypal-preconnect" rel="preconnect" href="https://www.paypal.com" crossOrigin="anonymous" />);
  elements.push(<link key="paypal-dns" rel="dns-prefetch" href="https://www.paypal.com" />);
  elements.push(<link key="paypal-objects-preconnect" rel="preconnect" href="https://www.paypalobjects.com" crossOrigin="anonymous" />);
  elements.push(<link key="paypal-objects-dns" rel="dns-prefetch" href="https://www.paypalobjects.com" />);
  
  // Cloudflare for images
  elements.push(<link key="cloudflare-preconnect" rel="preconnect" href="https://imagedelivery.net" crossOrigin="anonymous" />);
  elements.push(<link key="cloudflare-dns" rel="dns-prefetch" href="https://imagedelivery.net" />);
  
  // Basic meta tags
  elements.push(<title key="title">{fullTitle}</title>);
  elements.push(<meta key="description" name="description" content={metaDescription} />);
  elements.push(<meta key="exoclick" name="exoclick-site-verification" content={data.site.siteMetadata.exoclickSiteVerification} />);
  
  // Open Graph tags
  elements.push(<meta key="og-title" property="og:title" content={title} />);
  elements.push(<meta key="og-description" property="og:description" content={metaDescription} />);
  elements.push(<meta key="og-type" property="og:type" content="website" />);
  if (canonicalUrl) {
    elements.push(<meta key="og-url" property="og:url" content={canonicalUrl} />);
  }
  elements.push(<meta key="og-site" property="og:site_name" content={siteTitle} />);
  
  // Twitter card tags
  elements.push(<meta key="twitter-card" name="twitter:card" content="summary" />);
  elements.push(<meta key="twitter-creator" name="twitter:creator" content={data.site.siteMetadata?.social?.twitter || ``} />);
  elements.push(<meta key="twitter-title" name="twitter:title" content={title} />);
  elements.push(<meta key="twitter-description" name="twitter:description" content={metaDescription} />);
  
  // Other meta tags
  elements.push(<meta key="rating" name="RATING" content={data.site.siteMetadata.rating} />);
  elements.push(<meta key="release" name="release" content={gitCommitHash} />);
  
  // Canonical URL
  if (canonicalUrl) {
    elements.push(<link key="canonical" rel="canonical" href={canonicalUrl} />);
  }
  
  // Additional meta tags
  meta.forEach((metaItem, i) => {
    elements.push(<meta key={`meta-${i}`} {...metaItem} />);
  });
  
  // Structured data JSON-LD script
  if (structuredData) {
    elements.push(
      <script 
        key="jsonld-script" 
        type="application/ld+json" 
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} 
      />
    );
  }
  
  return elements;
}

Seo.propTypes = {
  description: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  canonicalUrl: PropTypes.string,
  structuredData: PropTypes.object,
}

export default Seo
